import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Envelope, CheckCircle, Warning, ArrowClockwise,
  BellSlash, Bell, PaperPlaneTilt, Info,
} from "@phosphor-icons/react";
import client from "../lib/api";

const money = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Weekly QBO Sync Digest opt-in card + inline preview of THIS week's activity.
 *
 * Auto-hides when the user's QBO integration is not connected — this card
 * makes no sense until there's actual sync activity to recap.
 *
 * Renders:
 *   - Toggle to enable/disable weekly digest (default on)
 *   - Live preview of the last 7 days: N invoices synced, N payroll JEs synced,
 *     failure counts, dollar totals
 *   - "Send now" button that fires the current draft to the caller's email
 */
export default function QboDigestCard() {
  const [qboStatus, setQboStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [optOut, setOptOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [status, prev, me] = await Promise.all([
        client.get("/qbo/status").then((r) => r.data).catch(() => null),
        client.get("/qbo-digest/preview").then((r) => r.data).catch(() => null),
        client.get("/auth/me").then((r) => r.data).catch(() => null),
      ]);
      setQboStatus(status);
      setPreview(prev);
      setOptOut(!!me?.weekly_qbo_digest_opt_out);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-hide when QBO isn't connected — nothing to digest
  if (loading && !preview) return null;
  if (!qboStatus || qboStatus.configured === false || qboStatus.connected === false) return null;

  const t = preview?.totals || {};
  const failCount = (t.failed_invoices || 0) + (t.failed_payroll || 0);
  const activityCount = t.total_activity || 0;

  const doToggle = async () => {
    setToggling(true);
    try {
      const url = optOut ? "/qbo-digest/opt-in" : "/qbo-digest/opt-out";
      await client.post(url);
      setOptOut(!optOut);
      toast.success(optOut ? "Weekly QBO digest ON" : "Weekly QBO digest OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setToggling(false); }
  };

  const doSendNow = async () => {
    if (activityCount === 0) {
      toast.error("Nothing to send this week — sync some invoices or payroll first.");
      return;
    }
    setSending(true);
    try {
      const { data } = await client.post("/qbo-digest/send");
      if (data.status === "sent") {
        toast.success(`Digest sent — ${activityCount} item${activityCount !== 1 ? "s" : ""}`);
      } else if (data.status === "queued") {
        toast.warning("Digest queued but email transport not configured");
      } else {
        toast.info(`Skipped: ${data.reason || "unknown"}`);
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div
      className="card-surface p-5 flex flex-col gap-4"
      data-testid="qbo-digest-card"
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#FF5A1F]/15 text-[#FF9764] flex items-center justify-center flex-shrink-0">
          <Envelope size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold">Weekly QuickBooks digest</h3>
            {optOut ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-neutral-500 uppercase tracking-widest">
                Paused
              </span>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 uppercase tracking-widest">
                Active
              </span>
            )}
          </div>
          <p className="text-xs text-neutral-500 mt-1 max-w-md">
            Every Friday 5pm CT — a plain-English recap of everything pushed to QuickBooks that week.
            Clean audit trail for your CPA, and any failed syncs get flagged at the top.
          </p>
        </div>
        <button
          type="button"
          onClick={loadAll}
          disabled={loading}
          data-testid="qbo-digest-refresh-btn"
          className="text-neutral-500 hover:text-white disabled:opacity-50 flex-shrink-0"
          title="Refresh preview"
        >
          <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Preview stats */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-white/5"
        data-testid="qbo-digest-preview-stats"
      >
        <div>
          <div className="text-[10px] text-emerald-400 uppercase tracking-widest font-semibold">
            Invoices
          </div>
          <div className="text-white text-xl font-semibold" data-testid="qbo-digest-invoice-count">
            {t.synced_invoices || 0}
          </div>
          <div className="text-[11px] text-neutral-500 font-mono">
            {money(t.total_invoice_amount)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-[#FF9764] uppercase tracking-widest font-semibold">
            Payroll JEs
          </div>
          <div className="text-white text-xl font-semibold" data-testid="qbo-digest-payroll-count">
            {t.synced_payroll || 0}
          </div>
          <div className="text-[11px] text-neutral-500 font-mono">
            {money(t.total_payroll_gross)} gross
          </div>
        </div>
        <div>
          <div className={`text-[10px] uppercase tracking-widest font-semibold ${
            failCount > 0 ? "text-amber-400" : "text-neutral-500"
          }`}>
            Failed
          </div>
          <div className={`text-xl font-semibold ${
            failCount > 0 ? "text-amber-300" : "text-neutral-600"
          }`} data-testid="qbo-digest-fail-count">
            {failCount}
          </div>
          <div className="text-[11px] text-neutral-500">
            {failCount > 0 ? "Needs attention" : "All clean"}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-neutral-400 uppercase tracking-widest font-semibold">
            Window
          </div>
          <div className="text-white text-sm font-medium mt-0.5">
            {preview?.week_start?.slice(5, 10) || "—"}
          </div>
          <div className="text-[11px] text-neutral-500">
            → {preview?.week_end?.slice(5, 10) || "—"}
          </div>
        </div>
      </div>

      {/* Failed-sync callout when applicable */}
      {failCount > 0 && (
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2"
          data-testid="qbo-digest-fail-callout"
        >
          <Warning size={14} className="text-amber-300 flex-shrink-0 mt-0.5" weight="fill" />
          <div className="text-xs text-amber-100/80">
            <b className="text-amber-200">{failCount} failed sync{failCount !== 1 ? "s" : ""}</b>{" "}
            this week — check the details in your Friday digest email, or click{" "}
            <b>Send now</b> below to preview them right away.
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/5">
        <button
          onClick={doToggle}
          disabled={toggling}
          data-testid="qbo-digest-toggle-btn"
          className={`text-xs rounded-full px-3 py-1.5 border inline-flex items-center gap-1.5 transition-colors ${
            optOut
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
              : "border-white/10 text-neutral-400 hover:border-white/20"
          } disabled:opacity-60`}
        >
          {toggling ? (
            <div className="w-3 h-3 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          ) : optOut ? (
            <Bell size={12} weight="bold" />
          ) : (
            <BellSlash size={12} weight="bold" />
          )}
          {optOut ? "Turn digest ON" : "Turn digest OFF"}
        </button>

        <button
          onClick={doSendNow}
          disabled={sending || activityCount === 0}
          data-testid="qbo-digest-send-now-btn"
          className={`text-xs rounded-full px-4 py-1.5 inline-flex items-center gap-1.5 transition-colors ${
            activityCount === 0
              ? "border border-white/10 text-neutral-600 cursor-not-allowed"
              : "bg-[#FF5A1F] hover:bg-[#FF6B33] text-white font-semibold"
          } disabled:opacity-60`}
        >
          {sending ? (
            <>
              <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <PaperPlaneTilt size={12} weight="bold" />
              {activityCount === 0 ? "No activity to send" : "Send test to my email"}
            </>
          )}
        </button>

        <div
          className="flex items-center gap-1.5 text-[11px] text-neutral-500 ml-auto"
          data-testid="qbo-digest-schedule-hint"
        >
          <Info size={11} />
          Next auto-send · Fri 5pm CT
        </div>
      </div>
    </div>
  );
}
